// Reading custom fields on the client.  tickets[9399]
//
// The database owns the shape and checks every value (0064). These are the
// presentation rules on top: which fields a ticket or a department view shows,
// how a value reads, sorts and filters. Pure, so the rules are pinned by tests
// rather than by whichever component happens to draw them first.

import { ancestorIds, flattenTree, subtreeIds, type TreeNode } from "@/lib/pageTree";
import type { HeaderFilterOption } from "@/components/backlog/HeaderFilter";

export type CustomFieldType = "text" | "number" | "date" | "select" | "checkbox";

/** The types, with labels and a one-line hint, for the field editor. */
export const CUSTOM_FIELD_TYPES: ReadonlyArray<{ value: CustomFieldType; label: string; hint: string }> = [
  { value: "text", label: "Text", hint: "A short line of text, up to 500 characters." },
  { value: "number", label: "Number", hint: "Sorts numerically and filters by range." },
  { value: "date", label: "Date", hint: "A day, without a time." },
  { value: "select", label: "Pick-list", hint: "One choice from a list of coloured options." },
  { value: "checkbox", label: "Checkbox", hint: "Yes or no." },
];

export const fieldTypeLabel = (type: CustomFieldType): string =>
  CUSTOM_FIELD_TYPES.find((t) => t.value === type)?.label ?? type;

/** The badge colour of an option with none of its own. */
export const NEUTRAL_OPTION_COLOR = "#6B7280";

export interface CustomFieldOption {
  id: string;
  label: string;
  color: string | null;
  order_index: number;
}

export interface CustomField {
  id: string;
  name: string;
  description: string | null;
  field_type: CustomFieldType;
  show_in_list: boolean;
  order_index: number;
  /** Pick-list options in order; empty for every other type. */
  options: CustomFieldOption[];
  /** Departments the field is switched on for. Each also covers its sub-departments. */
  subsheetIds: string[];
}

/** A custom_fields row with its options and departments embedded. */
export interface CustomFieldRow {
  id: string;
  name: string;
  description: string | null;
  field_type: string;
  show_in_list: boolean;
  order_index: number;
  custom_field_options?: CustomFieldOption[] | null;
  custom_field_subsheets?: Array<{ subsheet_id: string }> | null;
}

export function toCustomField(row: CustomFieldRow): CustomField {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    field_type: row.field_type as CustomFieldType,
    show_in_list: row.show_in_list,
    order_index: row.order_index,
    options: [...(row.custom_field_options ?? [])].sort(
      (a, b) => a.order_index - b.order_index || a.label.localeCompare(b.label),
    ),
    subsheetIds: (row.custom_field_subsheets ?? []).map((s) => s.subsheet_id),
  };
}

/** A ticket's values, keyed by field id. */
export type CustomFieldValues = Record<string, unknown>;

/**
 * backlog_items.custom_fields as the app holds it: always an object. jsonb
 * arrives as one; anything else (a row built by hand, say) reads as no values.
 * Applied once, in normalizeBacklogItem.
 */
export const customFieldValues = (raw: unknown): CustomFieldValues =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as CustomFieldValues) : {};

export const isEmptyValue = (value: unknown): boolean => value === undefined || value === null || value === "";

/**
 * Every department a field applies in: the ones it is switched on for and
 * everything nested beneath them (decision 2).
 */
export function coveredDepartments(field: CustomField, pages: readonly TreeNode[]): Set<string> {
  const covered = new Set<string>();
  for (const id of field.subsheetIds) for (const d of subtreeIds(pages, id)) covered.add(d);
  return covered;
}

/**
 * Which of `fields` a ticket in these departments shows — the union over its
 * departments (decision 3), in field order. Coverage is worked out once per
 * field and answers are cached per department combination, so a list of
 * thousands of rows asks it cheaply.
 */
export function makeFieldResolver(
  fields: readonly CustomField[],
  pages: readonly TreeNode[],
): (subsheetIds: readonly string[]) => CustomField[] {
  const coverage = fields.map((f) => [f, coveredDepartments(f, pages)] as const);
  const cache = new Map<string, CustomField[]>();
  return (subsheetIds) => {
    const key = subsheetIds.join(",");
    let found = cache.get(key);
    if (!found) {
      found = coverage.filter(([, covered]) => subsheetIds.some((id) => covered.has(id))).map(([f]) => f);
      cache.set(key, found);
    }
    return found;
  };
}

/** One-off form of makeFieldResolver, for a single ticket. */
export const fieldsForDepartments = (
  fields: readonly CustomField[],
  subsheetIds: readonly string[],
  pages: readonly TreeNode[],
): CustomField[] => makeFieldResolver(fields, pages)(subsheetIds);

/**
 * A field's own departments after ticking or unticking one in the tree. Ticking
 * covers the department and everything beneath it, so its descendants' own rows
 * become redundant and are dropped. Unticking a department covered through a
 * parent moves the field down to that parent's other children instead (the
 * plan's decision 2), level by level, until only the unticked one is left out.
 */
export function toggleFieldDepartment(
  own: ReadonlySet<string>,
  subsheetId: string,
  on: boolean,
  pages: readonly TreeNode[],
): Set<string> {
  const next = new Set(own);
  const childrenOf = (id: string) => pages.filter((p) => p.parent_id === id).map((p) => p.id);
  if (on) {
    next.add(subsheetId);
  } else {
    // Swap the nearest covering ancestor for its children, and repeat until
    // nothing above the department covers it any more.
    for (;;) {
      const covering = [...ancestorIds(pages, subsheetId)].find((id) => next.has(id));
      if (!covering) break;
      next.delete(covering);
      for (const child of childrenOf(covering)) next.add(child);
    }
    next.delete(subsheetId);
  }
  // Keep only the topmost of any covered chain.
  for (const id of [...next]) {
    if ([...ancestorIds(pages, id)].some((a) => next.has(a))) next.delete(id);
  }
  return next;
}

/** Every department a field applies in, in tree order — what Settings lists under "Used in". */
export function departmentsWithField(field: CustomField, pages: readonly TreeNode[]): string[] {
  const covered = coveredDepartments(field, pages);
  return flattenTree(pages)
    .map(({ page }) => page.id)
    .filter((id) => covered.has(id));
}

/** A department view shows at most this many fields as columns of their own. */
export const MAX_FIELD_COLUMNS = 2;

/**
 * How the ticket list shows custom fields. Only fields with "Show as a column"
 * on, and only ones that apply somewhere in view (in a department view, the
 * department or one nested beneath it, whose tickets the view includes).
 *
 * A department view gives each its own column, up to MAX_FIELD_COLUMNS: every
 * list column but Task is fixed-width, so each field column comes out of the
 * title. Past that, and wherever the list mixes departments (`scope` null, All
 * items), they share one combined Fields column instead.
 */
export function listFieldLayout(
  fields: readonly CustomField[],
  scope: ReadonlySet<string> | null,
  pages: readonly TreeNode[],
): { columns: CustomField[]; summary: CustomField[] | null } {
  const shown = fields.filter((f) => f.show_in_list && f.subsheetIds.length > 0);
  if (!scope) return { columns: [], summary: shown.length > 0 ? shown : null };
  const inView = fieldsForDepartments(shown, [...scope], pages);
  return inView.length <= MAX_FIELD_COLUMNS ? { columns: inView, summary: null } : { columns: [], summary: inView };
}

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * How a value reads: an option's label, "Yes"/"No", a date as "Nov 2, 2026", a
 * number as typed. Null for an empty field, or for a value its field cannot
 * read (an option since deleted).
 */
export function formatFieldValue(field: CustomField, value: unknown): string | null {
  if (isEmptyValue(value)) return null;
  switch (field.field_type) {
    case "select":
      return field.options.find((o) => o.id === value)?.label ?? null;
    case "checkbox":
      return value === true ? "Yes" : value === false ? "No" : null;
    case "date":
      if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
      return DATE_FORMAT.format(new Date(`${value}T00:00:00Z`));
    case "number":
      return typeof value === "number" ? String(value) : null;
    default:
      return typeof value === "string" ? value : null;
  }
}

/** The option a pick-list value points at, for its colour. */
export const optionOf = (field: CustomField, value: unknown): CustomFieldOption | undefined =>
  field.field_type === "select" ? field.options.find((o) => o.id === value) : undefined;

/** An option's badge colour, neutral when it has none. */
export const optionColor = (option: Pick<CustomFieldOption, "color">): string => option.color ?? NEUTRAL_OPTION_COLOR;

/**
 * Turns what someone typed into the value to save, or an error to show. Text
 * and number inputs only — the date, pick-list and checkbox controls hand over
 * a value directly. An empty input is null: it empties the field.
 */
export function parseFieldInput(
  field: CustomField,
  raw: string,
): { ok: true; value: string | number | null } | { ok: false; error: string } {
  const text = raw.trim();
  if (text === "") return { ok: true, value: null };
  if (field.field_type === "number") {
    const n = Number(text.replace(/,/g, ""));
    return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: `${field.name} must be a number` };
  }
  if (text.length > 500) return { ok: false, error: `${field.name} is limited to 500 characters` };
  return { ok: true, value: text };
}

// One collator for every text comparison: localeCompare with options builds a
// new one per call, which a sort over thousands of rows makes thousands of times.
const TEXT_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const optionRanks = new WeakMap<CustomField, Map<unknown, number>>();
const optionRank = (field: CustomField, value: unknown): number => {
  let ranks = optionRanks.get(field);
  if (!ranks) {
    ranks = new Map(field.options.map((o, i) => [o.id, i]));
    optionRanks.set(field, ranks);
  }
  return ranks.get(value) ?? Number.MAX_SAFE_INTEGER;
};

/**
 * Ascending order of two filled values of one field; the list puts empty values
 * last and applies the direction itself, as it does for every sort. Pick-lists
 * follow their option order, checkboxes put No before Yes.
 */
export function compareFieldValues(field: CustomField, a: unknown, b: unknown): number {
  switch (field.field_type) {
    case "number":
      return Number(a) - Number(b);
    case "checkbox":
      return Number(a === true) - Number(b === true);
    case "select":
      return optionRank(field, a) - optionRank(field, b);
    case "date": {
      // ISO days order as strings.
      const x = String(a);
      const y = String(b);
      return x < y ? -1 : x > y ? 1 : 0;
    }
    default:
      // Text compares naturally: "Item 2" before "Item 10".
      return TEXT_COLLATOR.compare(String(a), String(b));
  }
}

/** The sort-stack column for a field. */
export const fieldSortColumn = (fieldId: string) => `field:${fieldId}` as const;

/** The field id of a sort-stack column, or null for a built-in column. */
export const fieldIdOfSortColumn = (column: string): string | null =>
  column.startsWith("field:") ? column.slice("field:".length) : null;

/**
 * One field's filter. Pick-lists and checkboxes match any of `values` (option
 * ids, NO_VALUE for an empty field, "true"/"false"); numbers and dates match
 * the inclusive `from`/`to` range, either end open.
 */
// A type, not an interface, so it stays assignable to the Json a saved view stores.
export type FieldFilter = {
  values?: string[];
  from?: string | null;
  to?: string | null;
};

/** The value a filter uses for "empty", alongside the option ids. */
export const NO_VALUE = "__empty__";

export const isFieldFilterSet = (filter: FieldFilter | undefined): boolean =>
  !!filter && ((filter.values?.length ?? 0) > 0 || !!filter.from || !!filter.to);

export function matchesFieldFilter(field: CustomField, value: unknown, filter: FieldFilter | undefined): boolean {
  if (!isFieldFilterSet(filter)) return true;
  if (field.field_type === "number" || field.field_type === "date") {
    if (isEmptyValue(value)) return false;
    const toComparable = (v: unknown) => (field.field_type === "number" ? Number(v) : String(v));
    const v = toComparable(value);
    if (filter!.from && v < toComparable(filter!.from)) return false;
    if (filter!.to && v > toComparable(filter!.to)) return false;
    return true;
  }
  // An unticked checkbox and one never touched both read as "No".
  const key = field.field_type === "checkbox" ? String(value === true) : isEmptyValue(value) ? NO_VALUE : String(value);
  return (filter!.values ?? []).includes(key);
}

/** The ticks a pick-list or checkbox header offers. */
export function fieldFilterOptions(field: CustomField): HeaderFilterOption[] {
  if (field.field_type === "checkbox") {
    return [
      { id: "true", label: "Yes" },
      { id: "false", label: "No" },
    ];
  }
  return [
    ...field.options.map((o) => ({ id: o.id, label: o.label, color: optionColor(o) })),
    { id: NO_VALUE, label: "Empty" },
  ];
}

/** How the two sort directions read for a field. */
export function fieldSortLabels(field: CustomField): [asc: string, desc: string] {
  switch (field.field_type) {
    case "number":
      return ["Lowest first", "Highest first"];
    case "date":
      return ["Earliest first", "Latest first"];
    case "checkbox":
      return ["No first", "Yes first"];
    case "select":
      return ["In option order", "Reverse option order"];
    default:
      return ["A → Z", "Z → A"];
  }
}

/** Text values of these fields, for the list search. */
export function searchableFieldText(fields: readonly CustomField[], values: CustomFieldValues): string {
  return fields
    .map((f) => formatFieldValue(f, values[f.id]))
    .filter((v): v is string => v !== null)
    .join(" ");
}

/** Filters stored in localStorage or a saved view, re-validated: neither is a typed channel. */
export function parseFieldFilters(raw: unknown): Record<string, FieldFilter> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, FieldFilter> = {};
  for (const [id, f] of Object.entries(raw as Record<string, unknown>)) {
    if (!f || typeof f !== "object") continue;
    const { values, from, to } = f as Record<string, unknown>;
    const filter: FieldFilter = {
      values: Array.isArray(values) ? values.filter((v): v is string => typeof v === "string") : undefined,
      from: typeof from === "string" ? from : null,
      to: typeof to === "string" ? to : null,
    };
    if (isFieldFilterSet(filter)) out[id] = filter;
  }
  return out;
}
